// Browser-side DWG → DXF converter using libdxfrw WASM
// Loaded from public/ as a classic script (avoids webpack bundling Node.js deps)
// Supports AutoCAD R14 to R2020 (.dwg) files

// DWG version codes → human-readable names
const DWG_VERSION_MAP = {
  AC1014: 'AutoCAD R14',
  AC1015: 'AutoCAD 2000',
  AC1018: 'AutoCAD 2004',
  AC1021: 'AutoCAD 2007',
  AC1024: 'AutoCAD 2010',
  AC1027: 'AutoCAD 2013',
  AC1032: 'AutoCAD 2018-2020',
  AC1037: 'AutoCAD 2021-2022',
  AC1043: 'AutoCAD 2023-2024',
  AC1050: 'AutoCAD 2025',
};
const SUPPORTED_VERSIONS = new Set(['AC1014','AC1015','AC1018','AC1021','AC1024','AC1027','AC1032']);

function detectDwgVersion(arrayBuffer) {
  try {
    const bytes = new Uint8Array(arrayBuffer, 0, 6);
    return String.fromCharCode(...bytes);
  } catch { return null; }
}

let _modulePromise = null;

function loadModule() {
  if (_modulePromise) return _modulePromise;
  _modulePromise = new Promise((resolve, reject) => {
    if (window._libdxfrw_module) {
      resolve(window._libdxfrw_module);
      return;
    }

    // Load the Emscripten JS file as a classic script from public/
    const script = document.createElement('script');
    script.src = `${process.env.PUBLIC_URL || ''}/libdxfrw.js`;
    script.onload = async () => {
      try {
        if (typeof window.createModule !== 'function') {
          throw new Error('createModule global bulunamadı — libdxfrw.js yüklenememiş olabilir');
        }
        // createModule is set as a global by the Emscripten MODULARIZE bundle
        const libdxfrw = await window.createModule({
          locateFile: (path) => `${process.env.PUBLIC_URL || ''}/${path}`,
        });
        window._libdxfrw_module = libdxfrw;
        resolve(libdxfrw);
      } catch (err) {
        _modulePromise = null; // allow retry
        reject(err);
      }
    };
    script.onerror = () => {
      _modulePromise = null; // allow retry
      reject(new Error('libdxfrw.js yüklenemedi'));
    };
    document.head.appendChild(script);
  });
  return _modulePromise;
}

/**
 * Convert a DWG File/Blob to DXF text string in the browser.
 * @param {File|Blob} dwgFile
 * @returns {Promise<string>} DXF content as text
 */
export async function convertDwgToDxfBrowser(dwgFile) {
  const arrayBuffer = await dwgFile.arrayBuffer();

  // Check DWG version before loading WASM
  const ver = detectDwgVersion(arrayBuffer);
  if (ver && !SUPPORTED_VERSIONS.has(ver)) {
    const verName = DWG_VERSION_MAP[ver] || ver;
    throw new Error(
      `DWG versiyonu desteklenmiyor: ${verName}. Tarayıcı dönüştürücü yalnızca R14–2020 arası destekler. Dosyayı AutoCAD/LibreCAD ile DXF olarak kaydedin.`
    );
  }

  const libdxfrw = await loadModule();

  let database, fileHandler;
  try {
    database = new libdxfrw.DRW_Database();
    fileHandler = new libdxfrw.DRW_FileHandler();
    fileHandler.database = database;

    const ok = fileHandler.fileImport(arrayBuffer, database, false, false);
    if (!ok) {
      throw new Error('DWG dosyası okunamadı (R14–R2020 arası desteklenir).');
    }

    // Export as DXF R2007 (AC1021)
    const dxfContent = fileHandler.fileExport(libdxfrw.DRW_Version.AC1021, false, database, false);
    return dxfContent;
  } catch (err) {
    // Numeric exceptions from WASM = C++ exception (likely unsupported DWG version)
    const msg = err instanceof Error
      ? err.message
      : `DWG formatı desteklenmiyor (tarayıcı dönüştürücü R14–R2020 arası destekler; daha yeni dosyalar için DXF olarak kaydedin)`;
    throw new Error(msg);
  } finally {
    try { fileHandler && fileHandler.delete(); } catch (_) {}
    try { database && database.delete(); } catch (_) {}
  }
}
